import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useConnection } from "../context/ConnectionContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import ChangePasswordModal from "../components/ChangePasswordModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Database,
  Server,
  Code2,
  History,
  Bookmark,
  FolderTree,
  LogOut,
  User,
  ChevronDown,
  BarChart3,
  Calendar,
  RefreshCw,
  CreditCard,
  AlertCircle,
  Key,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

const navItems = [
  {
    name: "Connections",
    path: "/dashboard/connections",
    icon: Server,
    description: "Manage database connections"
  },
  {
    name: "Explorer",
    path: "/dashboard/explorer",
    icon: FolderTree,
    description: "Browse tables & collections"
  },
  {
    name: "Query Editor",
    path: "/dashboard/query",
    icon: Code2,
    description: "Write and execute queries"
  },
  {
    name: "History",
    path: "/dashboard/history",
    icon: History,
    description: "View query history"
  },
  {
    name: "Saved Queries",
    path: "/dashboard/saved",
    icon: Bookmark,
    description: "Manage saved queries"
  },
  {
    name: "Metrics",
    path: "/dashboard/metrics",
    icon: BarChart3,
    description: "Performance analytics"
  },
];

const DashboardLayout = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const { selectedConnection, selectConnection, connections, updateConnections } = useConnection();
  const navigate = useNavigate();
  const location = useLocation();
  const [logoUrl, setLogoUrl] = useState(null);
  const [siteName, setSiteName] = useState("AIQueryStudio");
  const [lowCreditNotified, setLowCreditNotified] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Fetch connections and settings on mount
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
    const fetchConnections = async () => {
      try {
        const response = await axios.get(`${API_URL}/connections`, {
          headers: getAuthHeader(), withCredentials: true
        });
        updateConnections(response.data);
      } catch (error) {
        console.error("Failed to fetch connections:", error);
      }
    };
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        if (response.data.logo_url) setLogoUrl(response.data.logo_url);
        if (response.data.site_name) setSiteName(response.data.site_name);
      } catch {}
    };
    fetchConnections();
    fetchSettings();
  }, []);

  // Redirect to explorer if user has a saved connection and is on the dashboard index
  useEffect(() => {
    const savedConnId = localStorage.getItem("selectedConnectionId");
    if (savedConnId && location.pathname === "/dashboard") {
      // Pass current location.state through so ProtectedRoute keeps the user context
      navigate("/dashboard/explorer", { replace: true, state: location.state });
    }
  }, [location.pathname, navigate]);

  const handleLogout = async () => {
    // Navigate first, then logout to avoid ProtectedRoute redirect race condition
    navigate("/", { replace: true });
    // Small delay to ensure navigation starts before auth state changes
    setTimeout(async () => {
      await logout();
    }, 100);
  };

  // Format expiry date
  const formatExpiryDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return null;
    }
  };

  // Check if subscription is expired or expiring soon
  const getExpiryStatus = (dateStr) => {
    if (!dateStr) return 'inactive';
    try {
      const expiryDate = new Date(dateStr);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysUntilExpiry < 0) return 'expired';
      if (daysUntilExpiry <= 7) return 'expiring';
      return 'active';
    } catch {
      return 'inactive';
    }
  };

  const expiryDate = formatExpiryDate(user?.subscription_expires);
  const expiryStatus = getExpiryStatus(user?.subscription_expires);
  const hasAutoRenew = user?.auto_renew === true;

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  // Redirect to subscribe if credits < 1
  useEffect(() => {
    if (user && !user.is_admin) {
      const creditsValue = user.credits ?? 0;
      if (creditsValue < 1) {
        if (!lowCreditNotified) {
          toast.error("You're out of credits. Top up to keep running queries.");
          setLowCreditNotified(true);
        }
        navigate('/subscribe', { replace: true, state: { reason: 'low-credits' } });
      }
    }
  }, [user, navigate, lowCreditNotified]);

  return (
    <div className="h-screen flex overflow-hidden bg-[#09090b]">
      {/* Sidebar */}
      <aside 
        className={`bg-card border-r border-border flex flex-col transition-all duration-300 ease-in-out ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/* Logo & Toggle */}
        <div className="h-16 flex items-center justify-between px-3 border-b border-border">
          {!sidebarCollapsed && (
            <button 
              onClick={() => navigate('/')} 
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              {logoUrl ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-6 h-6 object-contain mr-2" />
              ) : (
                <Database className="w-6 h-6 text-primary mr-2" />
              )}
              <span className="font-bold text-lg tracking-tight">{siteName}</span>
            </button>
          )}
          {sidebarCollapsed && (
            <button 
              onClick={() => navigate('/')} 
              className="mx-auto hover:opacity-80 transition-opacity"
            >
              {logoUrl ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt={siteName} className="w-6 h-6 object-contain" />
              ) : (
                <Database className="w-6 h-6 text-primary" />
              )}
            </button>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Active Connection Selector */}
        {!sidebarCollapsed && connections.length > 0 && (
          <div className="px-3 py-3 border-b border-border">
            <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">
              Active Connection
            </label>
            <Select 
              value={selectedConnection?.id || ""} 
              onValueChange={(id) => {
                const conn = connections.find(c => c.id === id);
                selectConnection(conn);
              }}
            >
              <SelectTrigger className="w-full" data-testid="global-connection-select">
                <SelectValue placeholder="Select connection" />
              </SelectTrigger>
              <SelectContent>
                {connections.map(conn => (
                  <SelectItem key={conn.id} value={conn.id}>
                    <div className="flex items-center gap-2">
                      {conn.db_type === 'mongodb' ? (
                        <Database className="w-4 h-4 text-green-500" />
                      ) : (
                        <Server className="w-4 h-4 text-blue-500" />
                      )}
                      <span className="truncate">{conn.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedConnection && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {selectedConnection.db_type === 'mongodb' ? 'MongoDB' : 'MS SQL'} • {selectedConnection.database}
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={({ isActive }) =>
                  `flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`
                }
                title={sidebarCollapsed ? item.name : ''}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.name}</span>}
              </NavLink>
            ))}
          </nav>
        </ScrollArea>

        {/* Credits */}
        {user && !user.is_admin && !sidebarCollapsed && (
          <div className="px-3 py-3 border-t border-border">
            <div className="p-3 rounded-lg bg-muted/30 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Credits</span>
                <span className="text-lg font-bold text-primary">{user.credits ?? 0}</span>
              </div>
              <button 
                onClick={() => navigate('/subscribe')}
                className="flex items-center justify-center gap-2 mt-1 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-colors w-full"
                data-testid="buy-credits-link"
              >
                <CreditCard className="w-4 h-4" />
                Buy Credits
              </button>
            </div>
          </div>
        )}

        {/* User section */}
        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start gap-2 px-3'} py-2 h-auto`}
                data-testid="user-menu-btn"
              >
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                )}
                {!sidebarCollapsed && (
                  <>
                    <div className="flex-1 text-left">
                      <p className="text-sm font-medium truncate">{user?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              {user?.auth_type === 'google' && (
                <p className="px-2 py-1 text-xs text-muted-foreground">Signed in with Google</p>
              )}
              <DropdownMenuSeparator />
              {user?.auth_type !== 'google' && (
                <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
                  <Key className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-dropdown-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Visible Logout Button */}
          <Button
            variant="outline"
            className="w-full mt-3 gap-2 text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <Outlet />
      </main>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        open={showChangePassword} 
        onOpenChange={setShowChangePassword}
        isAdmin={false}
      />
    </div>
  );
};

export default DashboardLayout;
